import React, { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import colors from 'styles/colors'
import {
  logScreenAction,
  trackingSubScreenKey
} from 'services/trackingScreenActions'
import RowStack from 'components/atoms/container/RowStack'
import BoxWithBorder from './BoxWithBorder'

const StyledAccordion = ({
  children,
  initialExpanded,
  summaryComponent,
  detailProps,
  trackingEvent,
  trackingScreenKey = trackingSubScreenKey,
  summaryProps,
  expandIconColor = colors.SecondaryUserText,
  sx = {},
  showExpandIcon = true,
  expandCallback = () => {},
  ...otherProps
}) => {
  const [expanded, setExpanded] = useState(initialExpanded)
  useEffect(() => {
    setExpanded(initialExpanded)
  }, [initialExpanded])

  const handleExpand = () => {
    if (!expanded && trackingEvent) {
      logScreenAction(`${trackingEvent}_expand`, null, trackingScreenKey)
    }
    setExpanded(!expanded)
    expandCallback(!expanded)
  }

  return (
    <Accordion
      expanded={expanded}
      onChange={handleExpand}
      sx={{
        width: '100%',
        boxShadow: 'none',
        color: colors.MidnightTeal400,
        ...sx
      }}
      {...otherProps}>
      <AccordionSummary
        expandIcon={
          showExpandIcon && <ExpandMoreIcon sx={{ color: expandIconColor }} />
        }
        aria-controls='panel1a-content'
        {...summaryProps}>
        {summaryComponent}
      </AccordionSummary>
      <AccordionDetails {...detailProps}>{children}</AccordionDetails>
    </Accordion>
  )
}

export const AccordionHead = ({
  icon,
  heading,
  headingProps = {},
  children
}) => (
  <RowStack justifyContent={'space-between'}>
    <RowStack spacing={4}>
      {icon && (
        <>
          {React.cloneElement(icon, {
            sx: { color: colors.MidnightTeal400 }
          })}
        </>
      )}
      <Typography variant='h4' {...headingProps}>
        {heading}
      </Typography>
    </RowStack>
    {children}
  </RowStack>
)

export const StyledAccordionJourney = ({
  borderColor = colors.Grey100,
  bgColor = colors.White,
  padding = 8,
  ...otherProps
}) => {
  return (
    <StyledAccordion
      detailProps={{ sx: { padding: 0 } }}
      {...otherProps}
      sx={{
        padding,
        borderRadius: '12px !important',
        border: `1px solid ${borderColor}`,
        backgroundColor: bgColor,
        margin: 0,
        '&.Mui-expanded': {
          margin: 0
        },
        '&::before': {
          display: 'none'
        },
        '& .MuiAccordionSummary-root': {
          minHeight: 'auto !important'
        },
        '& .MuiAccordionSummary-root.Mui-focusVisible': {
          backgroundColor: colors.Transparent
        },
        '& .MuiAccordionSummary-expandIconWrapper .MuiSvgIcon-root': {
          color: colors.MidnightTeal400
        },
        '& .MuiAccordionSummary-content': {
          margin: '0 !important'
        }
      }}
    />
  )
}

export const AccordionWithBorder = ({
  expanded,
  setExpanded,
  summaryTitle,
  summaryComponent,
  children,
  borderRadius = 4
}) => {
  const expandAccordion = () => {
    if (setExpanded) setExpanded(!expanded)
  }

  return (
    <BoxWithBorder
      borderWidth={1}
      borderColor={colors.Grey200}
      borderRadius={borderRadius}
      paddingY={8}>
      <Accordion
        sx={{ borderRadius: borderRadius, boxShadow: 'none' }}
        expanded={expanded}>
        <AccordionSummary
          expandIcon={
            setExpanded && (
              <ExpandMoreIcon
                onClick={expandAccordion}
                sx={{
                  ':hover': { cursor: 'pointer' }
                }}
              />
            )
          }
          onClick={() => {
            if (!summaryComponent) expandAccordion()
          }}
          sx={{
            paddingX: 8,
            border: 'none',
            '& .MuiAccordionSummary-content': {
              marginY: 0,
              '&.Mui-expanded': { minHeight: 0, marginY: 0 }
            },
            minHeight: 0,
            '&.Mui-expanded': { minHeight: 0 },
            ':hover': {
              cursor: summaryComponent ? 'default !important' : 'pointer'
            }
          }}>
          <RowStack>
            <Typography
              variant='h5'
              onClick={expandAccordion}
              sx={{
                ':hover': { cursor: setExpanded ? 'pointer' : 'default' }
              }}>
              {summaryTitle}
            </Typography>
            {summaryComponent}
          </RowStack>
        </AccordionSummary>

        <AccordionDetails>{children}</AccordionDetails>
      </Accordion>
    </BoxWithBorder>
  )
}

export default StyledAccordion
